import { Delay, Execute, Loop, SequenceChain, consumer, inject } from "@spawn/core";
import { GoldenBonusService } from "@spawn/golden-bonus";
import {
	LineStepperModule,
	endFirstStepperLoopEvent,
	hideAllSymbolsOnSpotlight,
	showSymbolsOnSpotlight,
} from "@spawn/slot-machine";
import { BaseSymbolTypes } from "../services/symbolService";

@consumer
export class AlternatingLineStepperModule extends LineStepperModule {
	@inject protected readonly goldenBonusService!: GoldenBonusService;

	public override async start(): Promise<void> {
		await this.lineStepperLoop?.play();
	}

	public override init(): void {
		const slotFace = this.reelController.getSlotFace();
		const symbolCoordinates: Array<{ x: number; y: number; symbolType?: string }[]> = [];

		const bonusSymbols = this.goldenBonusService.getBonusSymbols();
		const onlyCoins = bonusSymbols.filter((symbol) => symbol.symbol !== BaseSymbolTypes.ScatterFG);

		if (onlyCoins.length) {
			symbolCoordinates.push([]);
			for (const bonusSymbol of onlyCoins) {
				symbolCoordinates[0].push({ x: bonusSymbol.position.x, y: bonusSymbol.position.y });
			}
		}

		const scatterCoordinates = [
			...slotFace.flatMap((reel, x) =>
				reel.map((symbol, y) => (symbol === BaseSymbolTypes.Scatter ? { x, y, symbolType: BaseSymbolTypes.Scatter } : null))
			),
			...bonusSymbols.map((symbol) =>
				symbol.symbol === BaseSymbolTypes.ScatterFG ? { ...symbol.position, symbolType: BaseSymbolTypes.ScatterFG } : null
			),
		].filter(Boolean) as { x: number; y: number; symbolType: string }[];

		if (scatterCoordinates.length > 0) {
			symbolCoordinates.push(scatterCoordinates);
		}

		const paylineSymbols = this.filterDoubleSymbols(
			this.partyService.paylineWins
				.filter(({ winAmount }) => this.ignoreZeroWins() && winAmount && winAmount > 0)
				.flatMap((payline) => payline.symbolCoordinates)
		);
		if (paylineSymbols.length) {
			symbolCoordinates.push(paylineSymbols);
		}

		const animationSequence = symbolCoordinates.map((coords, index) => {
			const symbolCoordinatesDefaults = coords.filter((coordinates) => {
				const symbolType = coordinates.symbolType ?? slotFace[coordinates.x][coordinates.y];
				return !this.visibleMetaDataKeysBySymbol()[symbolType];
			});

			return new SequenceChain(
				new Execute(() => {
					this.eventDispatcher.dispatch(hideAllSymbolsOnSpotlight());

					this.eventDispatcher.dispatch(
						showSymbolsOnSpotlight({
							symbolCoordinates: symbolCoordinatesDefaults,
							symbolMetaDataKeys: this.visibleMetaDataKeys(),
							loopAnimations: true,
						})
					);

					for (const coordinates of coords) {
						const symbolType = coordinates.symbolType ?? slotFace[coordinates.x][coordinates.y];
						const symbolMetaDataKeys = this.visibleMetaDataKeysBySymbol()[symbolType];
						if (symbolMetaDataKeys) {
							this.eventDispatcher.dispatch(
								showSymbolsOnSpotlight({
									symbolCoordinates: [coordinates],
									symbolMetaDataKeys,
									loopAnimations: true,
								})
							);
						}
					}
				}),
				new Delay(this.lineStepDuration()),
				new Execute(() => {
					new Execute(() => {
						const isFirstLoopFinished =
							this.lineStepperLoop && this.lineStepperLoop?.loopsCompleted === 0 && index === symbolCoordinates.length - 1;
						if (isFirstLoopFinished) {
							this.eventDispatcher.dispatch(endFirstStepperLoopEvent());
						}
					});
				})
			);
		});

		this.lineStepperLoop = new Loop(new SequenceChain(...animationSequence));
	}
}
